import { Button } from 'primereact/button';
import { useState, useEffect } from 'react';
import { auth, db } from '../../config/firebase';
import { onSnapshot, doc } from 'firebase/firestore';
import axios from 'axios';
import { Hearts } from 'react-loader-spinner';

const UserSelection = ({ userName, partnerName, partnerId, onUserSelect, isLoading: externalLoading }) => {
  const [showUserSide, setShowUserSide] = useState(null);
  const [showPartnerSide, setShowPartnerSide] = useState(null);
  const [isCheckingStatus, setIsCheckingStatus] = useState(true);
  const [error, setError] = useState(null);
  const [userDefined, setUserDefined] = useState(false);
  const [minLoadingComplete, setMinLoadingComplete] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const minLoadingTimer = setTimeout(() => {
      if (isMounted) {
        setMinLoadingComplete(true);
      }
    }, 580);

    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (isMounted) {
        setUserDefined(!!user);
        if (!user) {
          setIsCheckingStatus(false);
          setShowUserSide(false);
          setShowPartnerSide(false);
        }
      }
    });

    const checkTrackingStatus = async () => {
      try {
        const userId = auth.currentUser?.uid;
        if (!userId) return;

        const userListener = onSnapshot(
          doc(db, "UserInformation", userId),
          (docSnapshot) => {
            if (docSnapshot.exists() && isMounted) {
              const userData = docSnapshot.data();
              setShowUserSide(!userData.noTrack);
            } else if (isMounted) {
              setShowUserSide(true);
            }
          },
          (error) => {
            console.error("Error listening to user tracking preferences:", error);
            if (isMounted) {
              setError('Failed to load user tracking preferences');
            }
          }
        );

        let partnerListener = null;
        if (partnerId) {
          partnerListener = onSnapshot(
            doc(db, "UserInformation", partnerId),
            (docSnapshot) => {
              if (docSnapshot.exists() && isMounted) {
                const partnerData = docSnapshot.data();
                setShowPartnerSide(!partnerData.noTrack);
              } else if (isMounted) {
                setShowPartnerSide(true);
              }
            },
            (error) => {
              console.error("Error listening to partner tracking preferences:", error);
              if (isMounted) {
                setShowPartnerSide(false);
              }
            }
          );
        } else if (isMounted) {
          setShowPartnerSide(false);
        }

        if (isMounted) {
          setIsCheckingStatus(false);
        }

        return () => {
          userListener();
          if (partnerListener) {
            partnerListener();
          }
        };
      } catch (error) {
        console.error('Error setting up tracking status listeners:', error);
        if (isMounted) {
          setError('Failed to load tracking preferences');
          setIsCheckingStatus(false);
        }
      }
    };

    if (auth.currentUser) {
      const cleanup = checkTrackingStatus();
      
      return () => {
        isMounted = false;
        unsubscribe();
        clearTimeout(minLoadingTimer);
        cleanup.then(cleanupFn => {
          if (cleanupFn && typeof cleanupFn === 'function') {
            cleanupFn();
          }
        });
      };
    }

    return () => {
      isMounted = false;
      unsubscribe();
      clearTimeout(minLoadingTimer);
    };
  }, [partnerId]);

  const handleUserSideClose = async () => {
    try {
      const userId = auth.currentUser?.uid;
      if (!userId) return;

      setShowUserSide(false);
      await axios.put(`https://api.frazza.online/user/${userId}`, {
        noTrack: true
      });
    } catch (error) {
      console.error('Error updating user tracking status:', error);
      setError('Failed to update tracking preference');
      setShowUserSide(true);
    }
  };

  const handlePartnerSideClose = async () => {
    try {
      if (!partnerId) return;

      setShowPartnerSide(false);
      await axios.put(`https://api.frazza.online/user/${partnerId}`, {
        noTrack: true
      });
    } catch (error) {
      console.error('Error updating partner tracking status:', error);
      setError('Failed to update partner tracking preference');
      setShowPartnerSide(true);
    }
  };

  const handleResetTracking = async () => {
    try {
      const userId = auth.currentUser?.uid;
      if (!userId) return;

      setShowUserSide(true);
      if (partnerId) setShowPartnerSide(true);

      await axios.put(`https://api.frazza.online/user/${userId}`, {
        noTrack: false
      });

      if (partnerId) {
        await axios.put(`https://api.frazza.online/user/${partnerId}`, {
          noTrack: false
        });
      }
    } catch (error) {
      console.error('Error resetting tracking status:', error);
      setError('Failed to reset tracking preferences');
    }
  };

  const showResetButton = showUserSide !== null && showPartnerSide !== null &&
      (!showUserSide || !showPartnerSide);

  const isActuallyLoading = !minLoadingComplete || isCheckingStatus || externalLoading ||
      !userDefined || showUserSide === null || showPartnerSide === null;

  if (isActuallyLoading) {
    return (
      <div className="cs-selection-page">
        <div className="cs-loading-container">
          <Hearts height="80" width="80" color="#f97316" ariaLabel="hearts-loading" visible={true} />
          <p className="cs-loading-text">Preparing your wellness space...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="cs-selection-page">
        <div className="cs-error-container">
          <div className="cs-error-icon">
            <i className="pi pi-exclamation-triangle"></i>
          </div>
          <h2>Something went wrong</h2>
          <p>{error}</p>
          <Button 
            label="Try Again" 
            icon="pi pi-refresh" 
            onClick={() => window.location.reload()}
            className="cs-btn-primary"
          />
        </div>
      </div>
    );
  }

  return (
    <div className="cs-selection-page">
      {/* Header */}
      <header className="cs-selection-header">
        <h1 className="cs-title">Cycle Wellness</h1>
        <p className="cs-subtitle">
          Track your cycle with care and understanding
        </p>
      </header>

      {/* Reset Button */}
      {showResetButton && (
        <Button
          icon="pi pi-refresh"
          className="cs-reset-btn"
          onClick={handleResetTracking}
          tooltip="Reset Tracking Options"
          tooltipOptions={{ position: 'left' }}
        />
      )}

      {/* Selection Cards */}
      <div className="cs-selection-container">
        {showUserSide && (
          <div className="cs-user-card cs-card-user">
            <button 
              className="cs-close-btn"
              onClick={handleUserSideClose}
              aria-label="Close"
            >
              <i className="pi pi-times"></i>
            </button>
            
            <div className="cs-card-icon cs-card-icon-user">
              <i className="pi pi-user"></i>
            </div>
            
            <h2 className="cs-card-title">{userName || 'My Cycle'}</h2>
            <p className="cs-card-description">
              View and manage your personal cycle tracking
            </p>
            
            <Button
              label="Open My Tracker"
              icon="pi pi-arrow-right"
              iconPos="right"
              onClick={() => onUserSelect('user')}
              className="cs-btn-primary"
              disabled={isActuallyLoading}
            />
          </div>
        )}

        {showPartnerSide && partnerId && (
          <div className="cs-user-card cs-card-partner">
            <button 
              className="cs-close-btn"
              onClick={handlePartnerSideClose}
              aria-label="Close"
            >
              <i className="pi pi-times"></i>
            </button>
            
            <div className="cs-card-icon cs-card-icon-partner">
              <i className="pi pi-heart"></i>
            </div>
            
            <h2 className="cs-card-title">{partnerName || 'Partner'}</h2>
            <p className="cs-card-description">
              Stay connected and supportive throughout their cycle
            </p>
            
            <Button
              label="View Cycle"
              onClick={() => onUserSelect('partner')}
              className="cs-btn-secondary"
              disabled={isActuallyLoading}
            />
          </div>
        )}

        {!showUserSide && !showPartnerSide && (
          <div className="cs-empty-state">
            <div className="cs-empty-icon">
              <i className="pi pi-info-circle"></i>
            </div>
            <h2>No Active Trackers</h2>
            <p>Click the reset button to enable cycle tracking</p>
          </div>
        )}
      </div>

      {/* Feature Highlights */}
      <div className="cs-features">
        <div className="cs-feature-item">
          <i className="pi pi-calendar cs-feature-icon"></i>
          <span className="cs-feature-text">Track Daily</span>
        </div>
        <div className="cs-feature-item">
          <i className="pi pi-chart-line cs-feature-icon"></i>
          <span className="cs-feature-text">View Insights</span>
        </div>
        <div className="cs-feature-item">
          <i className="pi pi-users cs-feature-icon"></i>
          <span className="cs-feature-text">Share with Partner</span>
        </div>
      </div>
    </div>
  );
};

export default UserSelection;
